import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { useGetDraftProofreadCycleStatusQuery } from '@/services/api/agentChatWorkspaceService';

interface CycleStatusData {
  current_mode: 'drafting' | 'proofreading' | 'reviewing' | 'completed';
  iteration_count: number;
  critical_issues_count: number;
  cycle_complete: boolean;
  final_reason: string;
  iterations_history: CycleIteration[];
  max_iterations?: number;
  quality_threshold?: number;
  focus_rules?: string[];
}

interface CycleIteration {
  iteration_number: number;
  mode: string;
  critical_issues_count: number;
  timestamp: string;
  draft_summary?: string;
  proofreading_summary?: string;
}

interface DraftProofreadCycleStatusProps {
  matterId: string;
  workspaceId: string;
  /** Polling interval in seconds */
  pollInterval?: number;
  /** Callback when cycle completes */
  onCycleComplete?: (status: CycleStatusData) => void;
}

const MODE_ICONS = {
  drafting: FileText,
  proofreading: Search,
  reviewing: Clock,
  completed: CheckCircle,
};

const MODE_COLORS = {
  drafting: 'bg-blue-500',
  proofreading: 'bg-orange-500',
  reviewing: 'bg-yellow-500',
  completed: 'bg-green-500',
};

const MODE_LABELS = {
  drafting: 'Drafting',
  proofreading: 'Proofreading',
  reviewing: 'Reviewing',
  completed: 'Completed',
};

export const DraftProofreadCycleStatus: React.FC<
  DraftProofreadCycleStatusProps
> = ({ matterId, workspaceId, pollInterval = 5, onCycleComplete }) => {
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [previousStatus, setPreviousStatus] = useState<CycleStatusData | null>(
    null,
  );

  const {
    data: statusResponse,
    error,
    isLoading,
    refetch,
  } = useGetDraftProofreadCycleStatusQuery(
    { matterId, workspaceId },
    {
      pollingInterval: pollInterval * 1000,
      refetchOnMountOrArgChange: true,
    },
  );

  const status: CycleStatusData | null =
    statusResponse && statusResponse.status === 'found'
      ? (statusResponse as unknown as { cycle_status: CycleStatusData })
          .cycle_status
      : null;

  // Handle edge cases where status structure might be unexpected
  if (status) {
    if (!status.iterations_history) {
      status.iterations_history = [];
    }
    if (typeof status.iteration_count !== 'number') {
      status.iteration_count = 1;
    }
    if (typeof status.critical_issues_count !== 'number') {
      status.critical_issues_count = 0;
    }
    if (!status.current_mode) {
      status.current_mode = 'drafting';
    }
  }

  // Check for cycle completion
  useEffect(() => {
    if (status && previousStatus) {
      if (status.cycle_complete && !previousStatus.cycle_complete) {
        onCycleComplete?.(status);
        toast.success(
          `Draft-proofread cycle completed: ${status.final_reason}`,
        );
      }
    }
    setPreviousStatus(status);
  }, [status, onCycleComplete, previousStatus]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            Loading cycle status...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (error && 'status' in error && error.status !== 404) {
    // Derive a readable error message without nested ternaries
    let errorMessage = 'Unknown error';
    if ('data' in error && error.data) {
      errorMessage =
        typeof error.data === 'string'
          ? error.data
          : JSON.stringify(error.data);
    }

    return (
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center text-red-600">
            <XCircle className="mr-2 h-4 w-4" />
            Error Loading Status
          </CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-600">
            <Clock className="mr-2 h-4 w-4" />
            No Active Cycle
          </CardTitle>
          <CardDescription>
            No draft-proofread cycle is currently running for this workspace.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const CurrentModeIcon =
    MODE_ICONS[status.current_mode as keyof typeof MODE_ICONS];
  const maxIterations = status.max_iterations || 5;
  const progressPercentage = status.cycle_complete
    ? 100
    : (status.iteration_count / maxIterations) * 100;

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <CurrentModeIcon className="mr-2 h-5 w-5" />
            Draft-Proofread Cycle
          </CardTitle>
          <Badge
            variant={status.cycle_complete ? 'default' : 'secondary'}
            className={`${MODE_COLORS[status.current_mode as keyof typeof MODE_COLORS]} text-white`}
          >
            {MODE_LABELS[status.current_mode as keyof typeof MODE_LABELS]}
          </Badge>
        </div>
        <CardDescription>
          {status.cycle_complete
            ? `Completed: ${status.final_reason}`
            : `Iteration ${status.iteration_count} - ${MODE_LABELS[status.current_mode as keyof typeof MODE_LABELS]} phase`}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span>{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Current Status */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Current Iteration</p>
            <p className="text-2xl font-bold">{status.iteration_count}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Critical Issues</p>
            <p className="text-2xl font-bold flex items-center">
              {status.critical_issues_count}
              {status.critical_issues_count > 0 && (
                <AlertCircle className="ml-2 h-4 w-4 text-orange-500" />
              )}
            </p>
          </div>
        </div>

        {/* Iteration History */}
        {status.iterations_history.length > 0 && (
          <Collapsible
            open={isHistoryExpanded}
            onOpenChange={setIsHistoryExpanded}
          >
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                <Clock className="mr-2 h-4 w-4" />
                {isHistoryExpanded ? 'Hide' : 'Show'} Iteration History (
                {status.iterations_history.length})
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 mt-2">
              {status.iterations_history.map(
                (iteration: CycleIteration, index: number) => (
                  <div
                    key={index}
                    className="p-2 bg-gray-50 rounded-md text-sm space-y-1"
                  >
                    {/* Primary row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-2">
                          #{iteration.iteration_number}
                        </Badge>
                        <span className="capitalize">{iteration.mode}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {iteration.critical_issues_count > 0 && (
                          <span className="text-orange-600">
                            {iteration.critical_issues_count} issues
                          </span>
                        )}
                        <span className="text-gray-500">
                          {new Date(iteration.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>

                    {/* Optional summaries */}
                    {iteration.draft_summary &&
                      iteration.draft_summary.length > 0 && (
                        <p className="text-xs text-gray-600 pl-6">
                          Draft: {iteration.draft_summary}
                        </p>
                      )}
                    {iteration.proofreading_summary &&
                      iteration.proofreading_summary.length > 0 && (
                        <p className="text-xs text-gray-600 pl-6">
                          Proofread: {iteration.proofreading_summary}
                        </p>
                      )}
                  </div>
                ),
              )}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>

          {!status.cycle_complete && (
            <Button variant="outline" size="sm" disabled>
              <XCircle className="mr-2 h-4 w-4" />
              Stop Cycle
            </Button>
          )}
        </div>

        {/* Real-time indicator */}
        {!status.cycle_complete && (
          <div className="flex items-center text-xs text-gray-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
            Auto-refreshing every {pollInterval} seconds
          </div>
        )}
      </CardContent>
    </Card>
  );
};
